import {
  useCardSession,
  useCardSessionControls,
  useCardStatus,
  useStateValue,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { Fragment, useId } from 'react';

import { cn } from '@/lib/utils';

import { FieldError } from '../field';
import { fieldErrorId } from '../fieldIds';
import { useCardMetrics } from '../internals/useCardMetrics';
import { HELD_CLASS, sizingClass, TEXT_STYLE, TONE_CLASS } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

const NAME_CLASS =
  'underline-offset-2 transition-colors duration-[120ms] hover:enabled:text-foreground hover:enabled:underline motion-reduce:duration-[60ms]';

const isFilled = (value: unknown) =>
  typeof value === 'string' ? value.trim() !== '' : Boolean(value);

// Pressing a name focuses that field's path; a field bound with `hideWhenEmpty` draws
// itself while its path is focused, so it opens in place and leaves again empty
export const AddFields: ComponentFn<typeof catalog, 'AddFields'> = ({
  props,
}) => {
  const lineId = useId();
  const fieldValues = useStateValue<Record<string, unknown>>('/fields');
  const refusedFields = useStateValue<Record<string, string>>(
    '/validation_errors/fields'
  );
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const metrics = useCardMetrics();
  const empty = props.fields.filter(
    (entry) => !isFilled(fieldValues?.[entry.field])
  );
  if (empty.length === 0 || session.readOnly) return null;
  const held = status === 'running';

  return (
    <p
      className={cn(
        'min-h-[var(--ac-meta-lh)] text-foreground-muted',
        TEXT_STYLE.meta,
        held && HELD_CLASS,
        sizingClass(props.sizing)
      )}
    >
      + Add{' '}
      {empty.map((entry, index) => {
        // A hidden field cannot mark itself, so its name in the line carries the refusal
        const error = refusedFields?.[entry.field] || undefined;
        const nameId = `${lineId}-${entry.field}`;
        return (
          <Fragment key={entry.field}>
            {index > 0 ? ', ' : null}
            <button
              type="button"
              aria-label={`Add ${entry.label}`}
              aria-describedby={error ? fieldErrorId(nameId) : undefined}
              title={error}
              disabled={held}
              className={cn(
                NAME_CLASS,
                error && TONE_CLASS.danger,
                // The phone's 44-unit tap target extends past the line instead of making it taller
                metrics.mobile &&
                  'relative before:absolute before:inset-y-[calc(-14*var(--ac-u))] before:inset-x-0'
              )}
              onClick={() => controls.focus(`/fields/${entry.field}`)}
            >
              {entry.label}
            </button>
            <FieldError id={nameId} error={error} />
          </Fragment>
        );
      })}
    </p>
  );
};
